/**
 * AiModalManager - AI generation modal management
 *
 * Phase 5 Code Quality - Task 8.0
 *
 * Handles AI modal state, tab management, and coordinates AI operations
 * through callbacks. UI rendering remains in the toolbar for now due to
 * tight DOM coupling.
 *
 * Usage:
 *   const aiModal = new AiModalManager({
 *     onOpen: ({ blockId, blockType }) => { ... },
 *     onClose: () => { ... },
 *     onTabChange: (tab) => { ... },
 *     onGenerate: async ({ tab, prompt, provider, model, context }) => { ... },
 *     loadModels: async () => { ... },
 *     loadTemplates: async (blockType) => { ... }
 *   })
 *
 *   aiModal.open(blockId, blockType)
 *   aiModal.close()
 *   aiModal.switchTab('content')
 */

import { createLogger } from './logger.js'

const logger = createLogger('AiModalManager')

// Available tabs
export const AiTabs = {
  CONTENT: 'content',
  EDIT: 'edit',
  BLOCK: 'block'
}

// Tab labels for UI
export const TAB_LABELS = {
  [AiTabs.CONTENT]: 'Update Content',
  [AiTabs.EDIT]: 'Apply Changes',
  [AiTabs.BLOCK]: 'Redesign Block'
}

// Tone presets (matching WebsiteStyleGuide::INDUSTRY_PRESETS)
export const TONE_PRESETS = {
  default: { tone: 'professional', length: 'standard', urgency: 'none', keywords: []},
  healthcare: { tone: 'professional', length: 'detailed', urgency: 'low', keywords: ['trusted', 'experienced', 'certified']},
  home_services: { tone: 'friendly', length: 'standard', urgency: 'medium', keywords: ['reliable', 'fast', 'affordable']},
  legal: { tone: 'formal', length: 'detailed', urgency: 'none', keywords: ['experienced', 'proven', 'results']},
  restaurant: { tone: 'casual', length: 'concise', urgency: 'low', keywords: ['fresh', 'delicious', 'local']},
  ecommerce: { tone: 'conversational', length: 'concise', urgency: 'high', keywords: ['limited', 'exclusive', 'savings']},
  tech: { tone: 'professional', length: 'standard', urgency: 'low', keywords: ['innovative', 'cutting-edge', 'secure']},
  real_estate: { tone: 'professional', length: 'detailed', urgency: 'medium', keywords: ['exclusive', 'premier', 'dream']}
}

export class AiModalManager {
  /**
   * @param {Object} options
   * @param {Function} options.onOpen - Called when modal opens: ({ blockId, blockType }) => void
   * @param {Function} options.onClose - Called when modal closes: () => void
   * @param {Function} options.onTabChange - Called when tab changes: (tab) => void
   * @param {Function} options.onGenerate - Called to generate: async ({ tab, prompt, provider, model, context }) => void
   * @param {Function} options.loadModels - Load AI models: async () => providers[]
   * @param {Function} options.loadTemplates - Load templates: async (blockType) => templates[]
   * @param {Function} options.onStatusChange - Status updates: (message, type) => void
   */
  constructor(options = {}) {
    this.options = {
      onOpen: null,
      onClose: null,
      onTabChange: null,
      onGenerate: null,
      loadModels: async() => [],
      loadTemplates: async() => [],
      onStatusChange: null,
      ...options
    }

    // Current state
    this.isOpen = false
    this.currentTab = AiTabs.CONTENT
    this.currentContext = null // { blockId, blockType }
    this.isGenerating = false
    this.variantMode = false

    // Cached data
    this.modelsCache = null
    this.templatesCache = {}

    // Tone settings
    this.toneSettings = {
      tone: 'professional',
      length: 'standard',
      urgency: 'none',
      keywords: [],
      industry_preset: 'default'
    }

    logger.log('Initialized')
  }

  /**
   * Open the AI modal for a block
   * @param {string} blockId - Block ID
   * @param {string} blockType - Block type
   */
  async open(blockId, blockType) {
    if (this.isOpen) {
      logger.warn('Modal already open')
      return
    }

    this.currentContext = { blockId, blockType }
    this.isOpen = true

    // Reset to content tab (less destructive default)
    this.currentTab = AiTabs.CONTENT
    this.variantMode = false

    logger.log('Opening modal for block:', blockId, blockType)

    // Notify listener
    if (this.options.onOpen) {
      this.options.onOpen({ blockId, blockType })
    }

    // Load models if not cached
    if (!this.modelsCache) {
      try {
        this.modelsCache = await this.options.loadModels()
      } catch (err) {
        logger.error('Failed to load models:', err)
      }
    }

    // Load templates for this block type
    if (!this.templatesCache[blockType]) {
      try {
        this.templatesCache[blockType] = await this.options.loadTemplates(blockType)
      } catch (err) {
        logger.error('Failed to load templates:', err)
      }
    }
  }

  /**
   * Close the AI modal
   */
  close() {
    if (!this.isOpen) {
      return
    }

    this.isOpen = false
    this.currentContext = null
    this.isGenerating = false

    logger.log('Modal closed')

    if (this.options.onClose) {
      this.options.onClose()
    }
  }

  /**
   * Switch to a different tab
   * @param {string} tab - Tab name (content, edit, block)
   */
  switchTab(tab) {
    if (!Object.values(AiTabs).includes(tab)) {
      logger.warn('Invalid tab:', tab)
      return
    }

    this.currentTab = tab
    logger.log('Switched to tab:', tab)

    if (this.options.onTabChange) {
      this.options.onTabChange(tab)
    }
  }

  /**
   * Generate AI content based on current tab and settings
   * @param {Object} params
   * @param {string} params.prompt - User prompt
   * @param {string} params.provider - AI provider
   * @param {string} params.model - AI model
   */
  async generate({ prompt, provider, model }) {
    if (!this.currentContext) {
      logger.error('No context - modal not open')
      return
    }

    if (this.isGenerating) {
      logger.warn('Generation already in progress')
      return
    }

    this.isGenerating = true
    logger.log('Starting generation:', { tab: this.currentTab, provider, model })

    try {
      if (this.options.onGenerate) {
        await this.options.onGenerate({
          tab: this.currentTab,
          prompt,
          provider,
          model,
          context: this.currentContext,
          toneSettings: this.toneSettings,
          variantMode: this.variantMode
        })
      }
    } catch (err) {
      logger.error('Generation failed:', err)
      if (this.options.onStatusChange) {
        this.options.onStatusChange(`Generation failed: ${err.message}`, 'error')
      }
    } finally {
      this.isGenerating = false
    }
  }

  /**
   * Toggle variant mode (generate multiple variants)
   * @param {boolean} enabled
   */
  setVariantMode(enabled) {
    this.variantMode = enabled
    logger.log('Variant mode:', enabled)
  }

  /**
   * Update tone settings
   * @param {Object} settings
   */
  setToneSettings(settings) {
    this.toneSettings = { ...this.toneSettings, ...settings }
    logger.log('Tone settings updated:', this.toneSettings)
  }

  /**
   * Apply a tone preset
   * @param {string} presetKey - Preset name from TONE_PRESETS
   */
  applyTonePreset(presetKey) {
    const preset = TONE_PRESETS[presetKey]
    if (preset) {
      this.toneSettings = {
        ...preset,
        industry_preset: presetKey
      }
      logger.log('Applied tone preset:', presetKey)
    }
  }

  /**
   * Get cached models
   * @returns {Array|null}
   */
  getModels() {
    return this.modelsCache
  }

  /**
   * Get cached templates for a block type
   * @param {string} blockType
   * @returns {Array}
   */
  getTemplates(blockType) {
    return this.templatesCache[blockType] || []
  }

  /**
   * Clear caches (e.g., after settings change)
   */
  clearCaches() {
    this.modelsCache = null
    this.templatesCache = {}
    logger.log('Caches cleared')
  }

  /**
   * Get current context
   * @returns {Object|null} { blockId, blockType }
   */
  getContext() {
    return this.currentContext
  }

  /**
   * Get current tab
   * @returns {string}
   */
  getCurrentTab() {
    return this.currentTab
  }

  /**
   * Get button label for current tab
   * @returns {string}
   */
  getButtonLabel() {
    return TAB_LABELS[this.currentTab] || 'Apply'
  }

  /**
   * Check if modal is currently generating
   * @returns {boolean}
   */
  isCurrentlyGenerating() {
    return this.isGenerating
  }

  /**
   * Format block type for display
   * @param {string} blockType
   * @returns {string}
   */
  static formatBlockType(blockType) {
    return blockType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
  }

  /**
   * Destroy the manager
   */
  destroy() {
    this.close()
    this.clearCaches()
    logger.log('Destroyed')
  }
}
